import { History } from "lucide-react";
import { EmptyState } from "@/components/ui/feedback";
import { ErrorState, LoadingState, NotAvailableState } from "@/components/ui/system-state";
import { Table, TableWrapper } from "@/components/ui/table";

/**
 * The reconciliation run history's state surface (J7, /reconciliation/run-history).
 *
 * Display only. This component owns no fetching: the page/caller passes the runs
 * or the failure, so every branch is exercised by a deterministic test and no
 * branch can quietly invent a value. "empty" (a real API returned nothing),
 * "loading", "error" (a real call failed) and "not available" (no reconciliation
 * service exists in this environment) are kept distinguishable — see
 * page-delivery-pipeline.md G2 `defer` and the `NotAvailableState` doc comment
 * in components/ui/system-state.tsx.
 */

export type ReconciliationRunStatus = "completed" | "failed" | "running";

export type ReconciliationRun = {
  id: string;
  /** When the run started. Absent renders an honest "not reported". */
  startedAt?: string;
  status: ReconciliationRunStatus;
  /**
   * How long the run took, in milliseconds exactly as the API reported it.
   * `null` (or a negative/NaN value) renders an honest dash — a run still in
   * flight has no duration yet, and `0s` there would claim it finished instantly.
   */
  durationMs: number | null;
  /**
   * Why the run failed, as the backend reported it. Only meaningful on a failed
   * run; when a failed run carries none we say so rather than showing a blank.
   */
  failureReason?: string;
};

export type RunHistoryError = {
  message: string;
  /** Only when the gateway actually returned one — never synthesised. */
  supportReference?: string;
};

const NO_STARTED_AT = "Not reported";
const NO_FAILURE_REASON = "No failure reason was reported.";
/** A value that is genuinely unknown — never a zero, which would be a claim. */
const UNKNOWN = "—";

const STATUS_LABELS: Record<ReconciliationRunStatus, string> = {
  completed: "Completed",
  failed: "Failed",
  running: "Running",
};

const STATUS_TONES: Record<ReconciliationRunStatus, string> = {
  completed: "text-success",
  failed: "text-danger",
  running: "text-muted",
};

/**
 * Renders a duration for a human: `45s`, `1m 05s`, `1h 00m 09s`.
 *
 * Exported because it is the one place a millisecond count is turned into copy —
 * a page that prints `durationMs` itself would show `65000`, which no operator
 * can read, and would round differently on every surface that did it locally.
 * An unknown duration is not zero and must not render as one.
 */
export function formatRunDuration(durationMs: number | null | undefined): string {
  if (durationMs == null || !Number.isFinite(durationMs) || durationMs < 0) return UNKNOWN;
  if (durationMs < 1_000) return "<1s";

  const totalSeconds = Math.floor(durationMs / 1_000);
  const seconds = totalSeconds % 60;
  const totalMinutes = Math.floor(totalSeconds / 60);
  const minutes = totalMinutes % 60;
  const hours = Math.floor(totalMinutes / 60);

  if (hours > 0) {
    return `${hours}h ${String(minutes).padStart(2, "0")}m ${String(seconds).padStart(2, "0")}s`;
  }
  if (totalMinutes > 0) {
    return `${totalMinutes}m ${String(seconds).padStart(2, "0")}s`;
  }
  return `${seconds}s`;
}

function failureReasonFor(run: ReconciliationRun): string {
  if (run.status !== "failed") return UNKNOWN;
  const reason = run.failureReason?.trim();
  return reason ? reason : NO_FAILURE_REASON;
}

export function ReconciliationRunHistoryStateView({
  runs,
  loading,
  error,
  unavailableReason,
}: {
  /** `null` while unavailable/loading; `[]` means a real call returned no runs. */
  runs: ReconciliationRun[] | null;
  loading: boolean;
  error: RunHistoryError | null;
  /** Set when no reconciliation service exists in this environment (G2 defer). */
  unavailableReason?: string;
}) {
  if (unavailableReason) {
    return (
      <NotAvailableState
        title="Run history is not available yet"
        description="There is no reconciliation service in this environment: no run records, no run schedule, and no per-run outcome to read."
        reason={unavailableReason}
      />
    );
  }

  if (loading) {
    return (
      <div>
        <p className="mb-3 text-sm text-muted">Loading reconciliation runs...</p>
        <LoadingState rows={3} />
      </div>
    );
  }

  if (error) {
    return (
      <ErrorState
        title="Run history could not be loaded"
        description={error.message}
        // The reference is the one thing support can act on. When the gateway did
        // not return one we say so, rather than showing an empty label or a
        // made-up id.
        details={
          error.supportReference
            ? `Support reference: ${error.supportReference}`
            : "No support reference was returned for this failure."
        }
      />
    );
  }

  if (!runs || runs.length === 0) {
    return (
      <EmptyState
        icon={<History aria-hidden className="size-5 text-muted" />}
        title="No reconciliation runs yet"
        description="No reconciliation run has been recorded in this environment yet. Runs appear here as they complete."
      />
    );
  }

  return (
    <TableWrapper>
      <Table className="dense">
        <thead>
          <tr className="border-b border-line bg-bg text-left text-xs font-semibold text-muted">
            <th className="px-4 py-2.5">Run</th>
            <th className="px-4 py-2.5">Started</th>
            <th className="px-4 py-2.5">Status</th>
            <th className="px-4 py-2.5 text-right">Duration</th>
            <th className="px-4 py-2.5">Failure reason</th>
          </tr>
        </thead>
        <tbody>
          {runs.map((run) => (
            <tr key={run.id} className="border-b border-line last:border-b-0">
              <td className="px-4 py-2.5 font-mono text-[13px] text-ink">{run.id}</td>
              <td className="px-4 py-2.5 text-[13px] text-muted">{run.startedAt ?? NO_STARTED_AT}</td>
              <td className={`px-4 py-2.5 text-[13px] font-semibold ${STATUS_TONES[run.status]}`}>
                {STATUS_LABELS[run.status]}
              </td>
              <td className="px-4 py-2.5 text-right font-mono text-[13px] text-ink">
                {formatRunDuration(run.durationMs)}
              </td>
              <td className="px-4 py-2.5 text-[13px] text-muted">{failureReasonFor(run)}</td>
            </tr>
          ))}
        </tbody>
      </Table>
    </TableWrapper>
  );
}
